import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// Utils
import globalModalManager from '../../utils/globalModalManager';

// UI
import TextareaAutosize from 'react-textarea-autosize';

// MUI
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Icons
import { FiSend, FiPaperclip, FiMic } from 'react-icons/fi';

// Components
import FilePreview from './FilePreview';

// Types
import { ChatMessage, UploadFile } from '../../types';
import Spinner from '../spinner';

interface ChatInputProps {
  loading?: boolean;
  disabled?: boolean;
  passedFiles?: File[];
  onMessageSend: (message: ChatMessage) => void;
}

const MAX_FILES = 10;
const PDF_MAX_SIZE = 30 * 1024 * 1024; // 30MB in bytes
const OTHER_FILES_MAX_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Allowed file types
const ALLOWED_FILE_EXTENSIONS = [
  '.pdf',
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp',
  '.doc', '.docx',
  '.txt',
  '.mp3', '.wav', '.m4a', '.aac', '.ogg',
  '.mp4', '.mov', '.avi', '.mkv', '.webm'
];

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'audio/mpeg', 'audio/wav', 'audio/x-m4a', 'audio/aac', 'audio/ogg',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm'
];

const ChatInput: React.FC<ChatInputProps> = ({
  loading = false,
  disabled = false,
  passedFiles = [],
  onMessageSend
}) => {

  // Hooks
  const { t } = useTranslation();

  // States
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [files, setFiles] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [showFileSizeRejectedSnackbar, setShowFileSizeRejectedSnackbar] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
  const [showFileTypeRejectedSnackbar, setShowFileTypeRejectedSnackbar] = useState(false);
  const [rejectedFileTypes, setRejectedFileTypes] = useState<File[]>([]);
  const [showFileLimitSnackbar, setShowFileLimitSnackbar] = useState(false);

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Utility functions
  const isPdfFile = useCallback((file: File): boolean => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  }, []);

  const validateFileSize = useCallback((file: File): boolean => {
    const maxSize = isPdfFile(file) ? PDF_MAX_SIZE : OTHER_FILES_MAX_SIZE;
    return file.size <= maxSize;
  }, [isPdfFile]);

  const validateFileType = useCallback((file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    // Check by MIME type first, then by extension as fallback
    return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_FILE_EXTENSIONS.includes(fileExtension);
  }, [ALLOWED_MIME_TYPES, ALLOWED_FILE_EXTENSIONS]);

  const validateFiles = useCallback((newFiles: File[]) => {
    const validFiles: File[] = [];
    const invalidSizeFiles: File[] = [];
    const invalidTypeFiles: File[] = [];

    // Check total file limit
    if (files.length + newFiles.length > MAX_FILES) {
      setShowFileLimitSnackbar(true);
      return { validFiles: [], invalidSizeFiles: [], invalidTypeFiles: [] };
    }

    newFiles.forEach(file => {
      if (!validateFileType(file)) {
        invalidTypeFiles.push(file);
      } else if (!validateFileSize(file)) {
        invalidSizeFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, invalidSizeFiles, invalidTypeFiles };
  }, [files, validateFileType, validateFileSize]);

  // Timer effect
  useEffect(() => {
    if (isRecording) {
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= 600) { // 10 minutes
            // Stop recording automatically
            if (mediaRecorderRef.current) {
              mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
            return 600;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (passedFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...passedFiles]);
    }
  }, [passedFiles]);

  // Memos
  const allowSend = useMemo(() => {
    return message.trim() || files.length > 0;
  }, [message, files]);

  // Error handlers
  const handleCloseFileSizeSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileSizeRejectedSnackbar(false);
    setRejectedFiles([]);
  }, []);

  const handleCloseFileTypeSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileTypeRejectedSnackbar(false);
    setRejectedFileTypes([]);
  }, []);

  const handleCloseFileLimitSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileLimitSnackbar(false);
  }, []);

  // Handlers
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      const { validFiles, invalidSizeFiles, invalidTypeFiles } = validateFiles(newFiles);

      // Show rejected file type snackbar if there are any
      if (invalidTypeFiles.length > 0) {
        setRejectedFileTypes(invalidTypeFiles);
        setShowFileTypeRejectedSnackbar(true);
      }

      // Show rejected file size snackbar if there are any
      if (invalidSizeFiles.length > 0) {
        setRejectedFiles(invalidSizeFiles);
        setShowFileSizeRejectedSnackbar(true);
      }

      // Only add valid files
      if (validFiles.length > 0) {
        setFiles(prevFiles => [...prevFiles, ...validFiles]);
      }
    }
  }, [files, validateFiles]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleSend = useCallback(() => {
    if (!allowSend) return;
    onMessageSend({ text: message, files: uploadedFiles });
    setMessage('');
    setFiles([]);
  }, [onMessageSend, message, uploadedFiles, allowSend]);

  const handleAttach = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileRemove = useCallback((file: any, signed_id?: string | number) => {
    setFiles(prevFiles => prevFiles.filter(f => f !== file));
    setUploadedFiles(prevFiles => prevFiles.filter(f => f.signed_id !== signed_id));
  }, []);

  const handleMic = useCallback(async () => {
    if (isRecording) {
      // Stop recording
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new window.MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, { type: 'audio/webm' });

        // TODO: Implement audio file upload logic
        setFiles(prevFiles => [...prevFiles, audioFile]);
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      globalModalManager.openModal('alert', { message: t('chatInput.microphoneAccessError') });
    }
  }, [isRecording]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const { validFiles, invalidSizeFiles, invalidTypeFiles } = validateFiles(newFiles);

    // Show rejected file type snackbar if there are any
    if (invalidTypeFiles.length > 0) {
      setRejectedFileTypes(invalidTypeFiles);
      setShowFileTypeRejectedSnackbar(true);
    }

    // Show rejected file size snackbar if there are any
    if (invalidSizeFiles.length > 0) {
      setRejectedFiles(invalidSizeFiles);
      setShowFileSizeRejectedSnackbar(true);
    }

    // Only add valid files
    if (validFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  }, [files, validateFiles]);

  const handleFileUploaded = useCallback((uploadedFile: UploadFile) => {
    setUploadedFiles(prevFiles => [...prevFiles, uploadedFile]);
  }, []);

  const handleMessageKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Format seconds as mm:ss
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Main Classes
  let mainClasses = 'flex flex-row flex-1 items-center gap-2 p-3 bg-gray-50 rounded-xl shadow-sm';
  const filePreviewClasses = 'flex flex-row gap-2 flex-wrap mb-2';
  const containerClasses = 'relative flex flex-col';
  if (isDragging) mainClasses += ' border-2 border-blue-500';
  const paperclipButtonClasses = 'bg-transparent border-none p-1 flex items-center text-gray-400 hover:text-gray-700';
  const messageInputClasses = 'flex-1 border-none rounded-md bg-transparent p-2';
  const recordingButtonClasses = `bg-transparent border-none p-1 flex items-center hover:scale-110 hover:text-red-500 transition-all duration-300 ${isRecording ? 'text-red-500' : 'text-gray-400'}`;
  const sendButtonClasses = 'ms-1 bg-transparent border-none p-1 flex items-center text-gray-400 hover:text-gray-700 hover:scale-110 transition-all duration-300 disabled:text-gray-400 disabled:cursor-not-allowed';

  // Render
  return (
    <div className={containerClasses}>
      {loading && <Spinner />}

      <div className={filePreviewClasses}>
        {files.map((file) => <FilePreview key={file.name} file={file} onRemove={handleFileRemove} onFileUploaded={handleFileUploaded} />)}
      </div>

      <div
        className={mainClasses}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        <TextareaAutosize
          className={messageInputClasses}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('chatInput.placeholder')}
          aria-label={t('chatInput.placeholder')}
          onKeyDown={handleMessageKeyDown}
          maxRows={10}
          minRows={1}
          disabled={disabled}
        />

        <div className="flex flex-col items-center justify-end">
          {isRecording && (
            <span className="text-xs text-red-500 mb-1 font-mono">{formatTime(recordingTime)}</span>
          )}

          <button
            className={recordingButtonClasses}
            onClick={handleMic}
            aria-label={isRecording ? t('chatInput.mic_stop') : t('chatInput.mic_record')}
            type="button"
            disabled={disabled}
          >
            <FiMic size={22} />

            {isRecording && (
              <span className="ms-1 animate-pulse text-xs">
                {t('chatInput.recording_indicator')}
              </span>
            )}
          </button>
        </div>

        <button
          className={paperclipButtonClasses}
          onClick={handleAttach}
          aria-label={t('chatInput.attach_file')}
          type="button"
          disabled={disabled}
        >
          <FiPaperclip size={22} />
        </button>

        <button
          type="button"
          onClick={handleSend}
          className={sendButtonClasses}
          disabled={!allowSend || disabled}
          aria-label={t('chatInput.send_message')}
        >
          <FiSend size={22} />
        </button>
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.gif,.svg,.webp,.doc,.docx,.txt,.mp3,.wav,.m4a,.aac,.ogg,.mp4,.mov,.avi,.mkv,.webm"
        disabled={disabled}
      />

      <Snackbar
        open={showFileSizeRejectedSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseFileSizeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileSizeSnackbar} severity="error" variant="filled">
          {rejectedFiles.length === 1
            ? t('attachMaterials.file_size_rejected_single', { fileName: rejectedFiles[0]?.name })
            : t('attachMaterials.file_size_rejected', { count: rejectedFiles.length })
          }
        </Alert>
      </Snackbar>

      <Snackbar
        open={showFileTypeRejectedSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseFileTypeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileTypeSnackbar} severity="error" variant="filled">
          {rejectedFileTypes.length === 1
            ? t('attachMaterials.file_type_rejected_single', { fileName: rejectedFileTypes[0]?.name })
            : t('attachMaterials.file_type_rejected', { count: rejectedFileTypes.length })
          }
        </Alert>
      </Snackbar>

      <Snackbar
        open={showFileLimitSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseFileLimitSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileLimitSnackbar} severity="warning" variant="filled">
          {t('attachMaterials.max_files_reached')}
        </Alert>
      </Snackbar>
    </div>

  );
}

export default ChatInput;
